import { Color } from "./Color";
import { Graphics2D } from "./Graphics2D";
import Matrix from "./Matrix";
import { Random } from "./Random";
import { Range, RangeType } from "./Range";

export class ParticleManager {
  public readonly random = new Random();
  public get width() {
    return this.graphics.width;
  }
  public get height() {
    return this.graphics.height;
  }
  constructor(private readonly graphics: Graphics2D) {}
}

export namespace vfx {
  export interface EmitItem {
    create(emitter: Emitter): EmitItemModule;
  }
  export interface EmitItemModule {
    update(elapsedTime: number): void;
  }
  export interface Item {
    create(particle: Particle): ItemModule;
  }
  export interface ItemModule {
    reset(): void;
    update(elapsedTime: number): void;
    draw?(graphics: Graphics2D): void;
  }

  // パーティクルの最小単位
  export class Particle {
    public x = 0;
    public y = 0;

    public offset_x = 0;
    public offset_y = 0;

    public yaw = 0;
    public pitch = 0;
    public roll = 0;

    public width = 10;
    public height = 10;

    public color = Color.White;
    public alive = false;

    private readonly modules: ItemModule[];
    private readonly renderModule: ItemModule | null = null;

    constructor(public readonly manager: ParticleManager, items: Item[]) {
      this.modules = new Array(items.length);
      for (let i = 0; i < items.length; i++) {
        this.modules[i] = items[i].create(this);
        if (this.modules[i].draw) {
          this.renderModule = this.modules[i];
        }
      }
    }

    // 毎フレーム揮発するパラメータ
    public reflesh() {
      this.offset_x = 0;
      this.offset_y = 0;
    }

    public reset() {
      this.alive = true;
      this.reflesh();
      for (const module of this.modules) {
        module.reset();
      }
    }

    // updateとresetは同じフレームで呼ばれることはない
    public update(elapsedTime: number) {
      this.reflesh();
      for (const module of this.modules) {
        module.update(elapsedTime);
      }
    }

    public draw(graphics: Graphics2D) {
      if (this.renderModule && this.renderModule.draw) {
        this.renderModule.draw(graphics);
      }
    }
  }

  export class Emitter {
    public intervalBias = 1.0;
    public spawnNum = 0;
    private readonly emitModules = new Array<EmitItemModule>();
    private readonly particles = new Array<vfx.Particle>();

    constructor(
      public readonly manager: ParticleManager,
      public readonly emits: EmitItem[],
      public readonly items: Item[]
    ) {
      for (const emit of emits) {
        this.emitModules.push(emit.create(this));
      }
    }

    public update(elapsedTime: number) {
      for (const emit of this.emitModules) {
        emit.update(elapsedTime);
      }

      for (const particle of this.particles) {
        if (particle.alive) {
          particle.update(elapsedTime);
        } else if (this.spawnNum > 0) {
          this.spawnNum--;
          particle.reset();
        }
      }

      // Spawn仕切らなかったら追加する
      while (this.spawnNum > 0) {
        this.spawnNum--;
        const particle = new vfx.Particle(this.manager, this.items);
        this.particles.push(particle);
        particle.reset();
      }
    }

    public draw(graphics: Graphics2D) {
      for (const particle of this.particles) {
        if (particle.alive) {
          particle.draw(graphics);
        }
      }
    }
  }

  //----------------------------------------------
  // このフレームでパーティクルをいくつ発生させるか管理する
  //----------------------------------------------
  export class SpawnItem implements EmitItem {
    constructor(public spawnNum: Range, public interval: Range) {}
    public create(emitter: Emitter) {
      return new SpawnModule(emitter, this);
    }
  }
  class SpawnModule implements EmitItemModule {
    private interval = 0;
    constructor(
      private readonly emitter: Emitter,
      private readonly item: SpawnItem
    ) {}
    public update(elapsedTime: number) {
      const rand = this.emitter.manager.random;

      this.emitter.spawnNum = 0;
      this.interval -= elapsedTime;
      while (this.interval <= 0.0) {
        this.interval +=
          rand.NextSingleRange(this.item.interval) * this.emitter.intervalBias;
        this.emitter.spawnNum += rand.NextIntRange(this.item.spawnNum);
      }
    }
  }

  //----------------------------------------------
  // 解像度によってSpawn頻度を変える
  //----------------------------------------------
  export class SpawnBiasItem implements EmitItem {
    constructor(public baseResolution: number) {}
    public create(emitter: Emitter) {
      return new SpawnBiasModule(emitter, this);
    }
  }
  class SpawnBiasModule implements EmitItemModule {
    constructor(
      private readonly emitter: Emitter,
      private readonly item: SpawnBiasItem
    ) {}
    public update(elapsedTime: number) {
      const factor = this.emitter.manager.width / this.item.baseResolution;
      this.emitter.intervalBias = 1.0 / Math.max(factor, 0.0000001);
    }
  }

  //----------------------------------------------
  // EmitterRangeの幅を変更する
  //----------------------------------------------
  export class EmitterRectWidthFullItem implements EmitItem {
    public create(emitter: Emitter) {
      return new EmitterRectWidthFullModule(emitter, this);
    }
  }
  class EmitterRectWidthFullModule implements EmitItemModule {
    private readonly emitterRect: EmitterRectItem | null;
    constructor(
      private readonly emitter: Emitter,
      private readonly item: EmitterRectWidthFullItem
    ) {
      this.emitterRect = this.emitter.items.find(
        (item) => item instanceof EmitterRectItem
      ) as EmitterRectItem;
    }
    public update(elapsedTime: number) {
      if (this.emitterRect) {
        this.emitterRect.x = new Range(
          RangeType.Span,
          0,
          this.emitter.manager.width
        );
      }
    }
  }

  //----------------------------------------------
  // パーティクルの初期位置を決定する
  //----------------------------------------------
  export class EmitterRectItem implements Item {
    constructor(public x: Range, public y: Range) {}
    public create(particle: Particle) {
      return new EmitterRectModule(particle, this);
    }
  }
  class EmitterRectModule implements ItemModule {
    constructor(
      private readonly particle: Particle,
      private readonly item: EmitterRectItem
    ) {}
    public reset() {
      const rand = this.particle.manager.random;
      this.particle.x = rand.NextSingleRange(this.item.x);
      this.particle.y = rand.NextSingleRange(this.item.y);
    }
    public update(elapsedTime: number) {}
  }

  //----------------------------------------------
  // 移動アニメーションを行うための速度
  //----------------------------------------------
  export class VelocityItem implements Item {
    constructor(public vx: Range, public vy: Range) {}
    public create(particle: Particle) {
      return new VelocityModule(particle, this);
    }
  }
  class VelocityModule implements ItemModule {
    private vx = 0.0;
    private vy = 0.0;

    constructor(
      private readonly particle: Particle,
      private readonly item: VelocityItem
    ) {}
    public reset() {
      const rand = this.particle.manager.random;
      this.vx = rand.NextSingleRange(this.item.vx);
      this.vy = rand.NextSingleRange(this.item.vy);
    }
    public update(elapsedTime: number) {
      this.particle.x += this.vx * elapsedTime;
      this.particle.y += this.vy * elapsedTime;
    }
  }

  //----------------------------------------------
  // 初期サイズ
  //----------------------------------------------
  export class SizeItem implements Item {
    constructor(
      public width: Range,
      public height: Range,
      public scale: Range
    ) {}
    public create(particle: Particle) {
      return new SizeModule(particle, this);
    }
  }
  class SizeModule implements ItemModule {
    constructor(
      private readonly particle: Particle,
      private readonly item: SizeItem
    ) {}
    public reset() {
      const rand = this.particle.manager.random;
      const scale = rand.NextSingleRange(this.item.scale);
      this.particle.width = rand.NextSingleRange(this.item.width) * scale;
      this.particle.height = rand.NextSingleRange(this.item.height) * scale;
    }
    public update(elapsedTime: number) {}
  }

  //----------------------------------------------
  // 指定カラーのリストからどれか一つ抽選する
  //----------------------------------------------
  export class ColorTableItem implements Item {
    constructor(public colors: Color[]) {}
    public create(particle: Particle) {
      return new ColorTableModule(particle, this);
    }
  }
  class ColorTableModule implements ItemModule {
    constructor(
      private readonly particle: Particle,
      private readonly item: ColorTableItem
    ) {}
    public reset() {
      const rand = this.particle.manager.random;
      if (this.item.colors.length === 0) {
        this.particle.color = Color.White;
      } else {
        const idx = rand.NextIntRange(0, this.item.colors.length - 1);
        this.particle.color = this.item.colors[idx];
      }
    }
    public update(elapsedTime: number) {}
  }

  //----------------------------------------------
  // カードとして描画するプリミティブ形状
  //----------------------------------------------
  export class CardItem implements Item {
    constructor(public frontToWhite: Range, public backToWhite: Range) {}
    public create(particle: Particle) {
      return new CardModule(particle, this);
    }
  }
  class CardModule implements ItemModule {
    constructor(
      private readonly particle: Particle,
      private readonly item: CardItem
    ) {}
    public reset() {}
    public update(elapsedTime: number) {}
    public draw(graphics: Graphics2D) {
      const x = this.particle.x + this.particle.offset_x;
      const y = this.particle.y + this.particle.offset_y;

      const m = new Matrix();
      m.setFromYawPitchRoll(
        this.particle.yaw,
        this.particle.pitch,
        this.particle.roll
      );
      m.mulScale(this.particle.width, this.particle.height, 1.0);
      m.setTranslation(x, y, 0.0);

      // Z軸が手前向き(マイナス方向)なら表
      if (m.m[10] <= 0.0) {
        graphics.fillCard(m, this.particle.color);
      } else {
        graphics.fillCard(m, Color.Lerp(this.particle.color, Color.White, 0.6));
      }
    }
  }

  //----------------------------------------------
  // 描画位置をパーティクル位置から更にずらす振れ幅
  //----------------------------------------------
  export class NoiseItem implements Item {
    constructor(
      public scaleX: Range,
      public scaleY: Range,
      public lowFrequency: Range,
      public lowFrequencyWidth: Range,
      public highFrequency: Range,
      public highFrequencyWidth: Range
    ) {}
    public create(particle: Particle) {
      return new NoiseModule(particle, this);
    }
  }
  class NoiseModule implements ItemModule {
    private scaleX = 1.0;
    private scaleY = 1.0;

    private lowFrequency = 0.0;
    private lowFrequencyWidth = 0.0;
    private highFrequency = 0.0;
    private highFrequencyWidth = 0.0;

    private totalTime = 0.0;

    constructor(
      private readonly particle: Particle,
      private readonly item: NoiseItem
    ) {}
    public reset() {
      const rand = this.particle.manager.random;

      this.scaleX = rand.NextSingleRange(this.item.scaleX);
      this.scaleY = rand.NextSingleRange(this.item.scaleY);

      this.lowFrequency =
        rand.NextSingleRange(this.item.lowFrequency) * rand.NextSign();
      this.lowFrequencyWidth = rand.NextSingleRange(
        this.item.lowFrequencyWidth
      );
      this.highFrequency =
        rand.NextSingleRange(this.item.highFrequency) * rand.NextSign();
      this.highFrequencyWidth = rand.NextSingleRange(
        this.item.highFrequencyWidth
      );

      // 一周するだけの乱数幅を決定
      let f = 1.0;
      if (this.lowFrequency !== 0 && this.highFrequency !== 0) {
        f = 1.0 / Math.min(this.lowFrequency, this.highFrequency);
      } else if (this.lowFrequency !== 0) {
        f = 1.0 / this.lowFrequency;
      } else if (this.highFrequency !== 0) {
        f = 1.0 / this.highFrequency;
      }
      this.totalTime = rand.NextInt() * f;
      this.setup();
    }
    public update(elapsedTime: number) {
      this.totalTime += elapsedTime;
      this.setup();
    }
    private setup() {
      const lx =
        Math.cos(this.lowFrequency * this.totalTime) * this.lowFrequencyWidth;
      const ly =
        Math.sin(this.lowFrequency * this.totalTime) * this.lowFrequencyWidth;
      const hx =
        Math.cos(this.highFrequency * this.totalTime) * this.highFrequencyWidth;
      const hy =
        Math.sin(this.highFrequency * this.totalTime) * this.highFrequencyWidth;

      this.particle.offset_x += (hx + lx) * this.scaleX;
      this.particle.offset_y += (hy + ly) * this.scaleY;
    }
  }

  //----------------------------------------------
  // 回転アニメーション
  //----------------------------------------------
  export class RotateAnimItem implements Item {
    constructor(public yaw: Range, public pitch: Range, public roll: Range) {}
    public create(particle: Particle) {
      return new RotateAnimModule(particle, this);
    }
  }
  class RotateAnimModule implements ItemModule {
    private yaw = 0;
    private pitch = 0;
    private roll = 0;

    constructor(
      private readonly particle: Particle,
      private readonly item: RotateAnimItem
    ) {}
    public reset() {
      const rand = this.particle.manager.random;
      const toRad = Math.PI / 180.0;
      this.yaw = rand.NextSingleRange(this.item.yaw) * toRad * rand.NextSign();
      this.pitch =
        rand.NextSingleRange(this.item.pitch) * toRad * rand.NextSign();
      this.roll =
        rand.NextSingleRange(this.item.roll) * toRad * rand.NextSign();
    }
    public update(elapsedTime: number) {
      this.particle.yaw += this.yaw * elapsedTime;
      this.particle.pitch += this.pitch * elapsedTime;
      this.particle.roll += this.roll * elapsedTime;
    }
  }

  //----------------------------------------------
  // 画面外に出たら消す
  //----------------------------------------------
  export class ScreenOutVanishItem implements Item {
    public create(particle: Particle) {
      return new ScreenOutVanishModule(particle, this);
    }
  }
  class ScreenOutVanishModule implements ItemModule {
    constructor(
      private readonly particle: Particle,
      private readonly item: ScreenOutVanishItem
    ) {}
    public reset() {}
    public update(elapsedTime: number) {
      if (this.particle.y > this.particle.manager.height * 1.1) {
        this.particle.alive = false;
      }
    }
  }
}
